import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

function Field({ testid, label, disabled, placeholder, options, selected, onSelect, error, onRetry, retryLabel, errorLabel }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-zinc-800">{label}</Label>
      <Select value={selected || undefined} onValueChange={onSelect} disabled={disabled}>
        <SelectTrigger data-testid={testid} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72 bg-white">
          {options.map((o) => (
            <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <div data-testid={`${testid}-error`} className="mt-1.5 flex items-center gap-2 text-xs text-red-600">
          <span>{errorLabel}</span>
          <Button type="button" size="sm" variant="outline" onClick={onRetry} className="h-6 px-2 rounded-full text-xs border-red-200 text-red-700 hover:bg-red-50">
            <RefreshCw className="w-3 h-3 mr-1" /> {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Five cascading combo boxes for Indonesian addresses:
 * Province -> Regency/City -> District -> Village -> Postal code.
 * Calls onChange(addressData) whenever the resolved selection changes.
 *
 * Every fetch is wrapped so a failure surfaces a visible error + retry button
 * instead of silently leaving the dropdown empty with no explanation.
 */
export default function AddressCascade({ value, onChange }) {
  const { t } = useLang();
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [villages, setVillages] = useState([]);
  const [errors, setErrors] = useState({ provinces: false, regencies: false, districts: false, villages: false });

  const setError = (key, hasError) => setErrors((e) => ({ ...e, [key]: hasError }));

  const loadProvinces = useCallback(() => {
    setError("provinces", false);
    api.get("/wilayah/provinces")
      .then((r) => setProvinces(r.data))
      .catch(() => { setProvinces([]); setError("provinces", true); });
  }, []);

  useEffect(() => { loadProvinces(); }, [loadProvinces]);

  const pick = (list, code) => list.find((x) => x.code === code);

  const loadRegencies = (provinceCode) => {
    setError("regencies", false);
    api.get("/wilayah/regencies", { params: { province_code: provinceCode } })
      .then((r) => setRegencies(r.data))
      .catch(() => { setRegencies([]); setError("regencies", true); });
  };

  const loadDistricts = (regencyCode) => {
    setError("districts", false);
    api.get("/wilayah/districts", { params: { regency_code: regencyCode } })
      .then((r) => setDistricts(r.data))
      .catch(() => { setDistricts([]); setError("districts", true); });
  };

  const loadVillages = (districtCode) => {
    setError("villages", false);
    api.get("/wilayah/villages", { params: { district_code: districtCode } })
      .then((r) => setVillages(r.data))
      .catch(() => { setVillages([]); setError("villages", true); });
  };

  const selectProvince = (code) => {
    const p = pick(provinces, code);
    onChange({
      province_code: code, province: p?.name || "",
      regency_code: "", regency: "", district_code: "", district: "",
      village_code: "", village: "", postal_code: "",
    });
    setDistricts([]); setVillages([]); setRegencies([]);
    loadRegencies(code);
  };

  const selectRegency = (code) => {
    const r = pick(regencies, code);
    onChange({
      ...value, regency_code: code, regency: r?.name || "",
      district_code: "", district: "", village_code: "", village: "", postal_code: "",
    });
    setVillages([]); setDistricts([]);
    loadDistricts(code);
  };

  const selectDistrict = (code) => {
    const d = pick(districts, code);
    onChange({
      ...value, district_code: code, district: d?.name || "",
      village_code: "", village: "", postal_code: "",
    });
    setVillages([]);
    loadVillages(code);
  };

  const selectVillage = (code) => {
    const v = pick(villages, code);
    onChange({ ...value, village_code: code, village: v?.name || "", postal_code: v?.postal_code || "" });
  };

  const errorLabel = t("checkout.addressLoadError");
  const retryLabel = t("checkout.retry");

  return (
    <div className="grid grid-cols-2 gap-4">
      <Field testid="checkout-province" label={t("checkout.province")} placeholder={t("checkout.provincePlaceholder")}
        options={provinces} selected={value.province_code} onSelect={selectProvince}
        error={errors.provinces} onRetry={loadProvinces} retryLabel={retryLabel} errorLabel={errorLabel} />
      <Field testid="checkout-regency" label={t("checkout.regency")} placeholder={t("checkout.regencyPlaceholder")}
        options={regencies} selected={value.regency_code} onSelect={selectRegency} disabled={!value.province_code}
        error={errors.regencies} onRetry={() => loadRegencies(value.province_code)} retryLabel={retryLabel} errorLabel={errorLabel} />
      <Field testid="checkout-district" label={t("checkout.district")} placeholder={t("checkout.districtPlaceholder")}
        options={districts} selected={value.district_code} onSelect={selectDistrict} disabled={!value.regency_code}
        error={errors.districts} onRetry={() => loadDistricts(value.regency_code)} retryLabel={retryLabel} errorLabel={errorLabel} />
      <Field testid="checkout-village" label={t("checkout.village")} placeholder={t("checkout.villagePlaceholder")}
        options={villages} selected={value.village_code} onSelect={selectVillage} disabled={!value.district_code}
        error={errors.villages} onRetry={() => loadVillages(value.district_code)} retryLabel={retryLabel} errorLabel={errorLabel} />
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("checkout.postalCode")}</Label>
        <Input
          data-testid="checkout-postal-code"
          value={value.postal_code || ""}
          readOnly
          disabled={!value.village_code}
          placeholder={t("checkout.postalCodePlaceholder")}
          className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11"
        />
      </div>
    </div>
  );
}
